import { useState } from 'react';
import {
  Loader2, Search, AlertTriangle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const BUCKET_LABELS = {
  supplier_present: { label: 'Supplier Present', color: 'text-green-600' },
  supplier_null_at_source: { label: 'Null at Source', color: 'text-yellow-600' },
  supplier_at_other_path: { label: 'At Other Path', color: 'text-red-500' },
  property_not_found: { label: 'Property Not Found', color: 'text-orange-500' },
};

const BUCKET_ORDER = [
  'supplier_at_other_path',
  'supplier_null_at_source',
  'supplier_present',
  'property_not_found',
];

function fmtGBP(v) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
  }).format(v);
}

// Simple heuristic: flag property names that look non-residential / test-like.
function looksTestLike(name) {
  if (!name) return false;
  const lower = name.toLowerCase();
  const hints = [
    'cycle path', 'test', 'demo', 'sample', 'placeholder', 'dummy',
    'parking', 'garage', 'storage', 'office', 'warehouse', 'commercial',
    'retail', 'shop', 'unit', 'plot', 'land',
  ];
  return hints.some(h => lower.includes(h));
}

export function NoSupplierAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tableOpen, setTableOpen] = useState(false);
  const [sortKey, setSortKey] = useState('amount');
  const [sortAsc, setSortAsc] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await diagnosticsAPI.noSupplierAudit();
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key !== 'amount'); // default desc for amount, asc for others
    }
  };

  const sortedRows = data?.rows
    ? [...data.rows].sort((a, b) => {
        let va = a[sortKey] ?? '';
        let vb = b[sortKey] ?? '';
        if (typeof va === 'number' && typeof vb === 'number') {
          return sortAsc ? va - vb : vb - va;
        }
        va = String(va).toLowerCase();
        vb = String(vb).toLowerCase();
        return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      })
    : [];

  const SortHeader = ({ field, children }) => (
    <th
      className="px-2 py-1 text-left font-medium cursor-pointer hover:text-foreground select-none"
      onClick={() => handleSort(field)}
    >
      {children}
      {sortKey === field && (
        <span className="ml-0.5">{sortAsc ? '\u25B2' : '\u25BC'}</span>
      )}
    </th>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">No-Supplier Batch Audit</CardTitle>
          <Button variant="outline" size="sm" onClick={runAudit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run audit
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Classifies every no-supplier booking by probing the BR OS API for each
          property. Determines whether suppliers are genuinely absent, null at source,
          or present at an unexpected path (which would justify a remap).
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {!data && !error && !loading && (
          <p className="text-sm text-muted-foreground">
            Press Run audit to classify all no-supplier bookings against the BR OS API.
          </p>
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {data?.api_error && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>API error: {data.api_error}</span>
          </div>
        )}

        {data && !data.api_error && (
          <div className="space-y-4">
            {/* Bucket totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {BUCKET_ORDER.map((bucket) => {
                const info = BUCKET_LABELS[bucket];
                const bt = data.bucket_totals?.[bucket] || { count: 0, total_gbp: 0 };
                return (
                  <div key={bucket} className="rounded-lg border p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      {info.label}
                    </p>
                    <p className={`text-lg font-semibold ${info.color}`}>
                      {bt.count}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {fmtGBP(bt.total_gbp)}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Summary line */}
            <div className="rounded-lg border p-3 bg-muted/30 text-sm">
              <span className="font-medium">{data.total_no_supplier_bookings}</span>
              {' '}bookings audited.
              {(data.bucket_totals?.supplier_at_other_path?.count || 0) === 0 && (
                <span className="ml-2 text-green-600 font-medium">
                  No supplier_at_other_path rows found. Remap hypothesis is dead.
                </span>
              )}
              {(data.bucket_totals?.supplier_at_other_path?.count || 0) > 0 && (
                <span className="ml-2 text-red-500 font-medium">
                  supplier_at_other_path rows found. Remap may be viable. See paths below.
                </span>
              )}
            </div>

            {/* Full table (collapsible) */}
            <div className="border rounded-md">
              <button
                onClick={() => setTableOpen(!tableOpen)}
                className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-left hover:bg-muted/50"
              >
                {tableOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Full booking table ({data.rows?.length || 0} rows)
              </button>
              {tableOpen && (
                <div className="overflow-x-auto border-t max-h-[600px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="bg-muted/30 border-b">
                        <SortHeader field="booking_id">Booking</SortHeader>
                        <SortHeader field="property_name">Property</SortHeader>
                        <SortHeader field="amount">Amount</SortHeader>
                        <SortHeader field="classification">Classification</SortHeader>
                        <th className="px-2 py-1 text-left font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row, i) => {
                        const isTest = looksTestLike(row.property_name);
                        const isOtherPath = row.classification === 'supplier_at_other_path';
                        const bucketInfo = BUCKET_LABELS[row.classification] || {};
                        return (
                          <tr
                            key={i}
                            className={[
                              'border-t',
                              isOtherPath ? 'bg-red-50 dark:bg-red-950/30' : '',
                              isTest ? 'bg-yellow-50/50 dark:bg-yellow-950/20' : '',
                            ].join(' ')}
                          >
                            <td className="px-2 py-1 font-mono whitespace-nowrap">
                              {row.booking_id}
                            </td>
                            <td className="px-2 py-1 max-w-xs">
                              <div className="font-medium truncate">
                                {row.property_name || row.property_id || '(unknown)'}
                                {isTest && (
                                  <span className="ml-1 text-yellow-600 text-[10px] font-medium">
                                    [test-like]
                                  </span>
                                )}
                              </div>
                              {row.address && (
                                <div className="text-muted-foreground truncate text-[10px]">
                                  {row.address}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1 font-mono text-right whitespace-nowrap">
                              {fmtGBP(row.amount)}
                              {row.currency && row.currency !== 'GBP' && (
                                <span className="text-muted-foreground ml-1">
                                  {row.currency}
                                </span>
                              )}
                            </td>
                            <td className={`px-2 py-1 whitespace-nowrap ${bucketInfo.color || ''}`}>
                              {bucketInfo.label || row.classification}
                            </td>
                            <td className="px-2 py-1">
                              {isOtherPath && row.found_supplier_fields && (
                                <div className="space-y-0.5">
                                  {row.found_supplier_fields.map((f, j) => (
                                    <div key={j} className="font-mono text-red-600">
                                      {f.path}: {typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value ?? 'null')}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {row.classification === 'property_not_found' && (
                                <span className="text-orange-500">
                                  No API record for {row.property_id}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
